import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';

import { EnvProvider } from '@zenbusiness/application-commons-env';
import PageError from '@zenbusiness/zui/PageError';
import PageLoading from '@zenbusiness/zui/PageLoading';

import Authenticated from '#client/App/Authenticated';
import BusinessEntityGate from '#client/App/BusinessEntityGate';
import ClickstreamWrapper from '#client/App/ClickstreamWrapper';
import PostRouting from '#client/App/PostRouting';
import { DEFAULT_SEGMENT } from '#client/build-env';
import GlobalComponents from '#client/components/GlobalComponents';
import Template from '#client/components/Template';
import * as Pages from '#client/pages';
import { DEPLOY_ENV } from '#client/public-env';

import * as MFE from './MFEs';
import redirects from './Redirects';

const router = createBrowserRouter([
  ...redirects,
  {
    /**
     * Routes which do not require authentication at page load
     */
    element: (
      <ClickstreamWrapper>
        <PostRouting />
      </ClickstreamWrapper>
    ),
    children: [
      { index: true, element: <Pages.Landing /> },
      { path: '/d/login', element: <Pages.Login /> },
      { path: '/d/logout', element: <Pages.Logout /> },
      { path: '/d/callback', element: <Pages.Callback /> },
      { path: '/dashboard/account-not-found', element: <Pages.AccountNotFound /> },
      { path: '/dashboard/account-not-found-mobile', element: <Pages.AccountNotFoundMobile /> }
    ]
  },
  {
    /**
     * Routes which require authentication
     */
    path: DEFAULT_SEGMENT,
    element: (
      <Authenticated>
        <Template>
          <GlobalComponents>
            <PostRouting />
          </GlobalComponents>
        </Template>
      </Authenticated>
    ),
    children: [
      {
        path: 'account',
        children: [
          { path: 'payments', element: <MFE.ZenDash exportName="AccountPayments" /> },
          { path: 'settings', element: <Pages.AccountSettings /> },
          { path: '*', element: <Navigate to="/d/account/settings" /> }
        ]
      },
      { path: 'companies', element: <MFE.ZenDash exportName="EntityOverview" /> },
      { path: 'download', element: <Pages.Download /> },
      { path: 'subscription-hub/*', element: <MFE.EcommServicesUi /> },
      { path: 'financial-readiness', element: <MFE.MoneyUi /> },
      { path: 'help', element: <Pages.Help /> },
      { path: 'marketing/:redirectTo', element: <Pages.Landing /> },
      { index: true, element: <Pages.Landing /> }
    ]
  },
  {
    /**
     * Routes which can support authentication but do not require it
     */
    path: DEFAULT_SEGMENT,
    element: (
      <Authenticated optional>
        <PostRouting />
      </Authenticated>
    ),
    children: [
      { index: true, element: <Pages.Landing /> },
      { path: 'dashboard', element: <Pages.Landing /> }
    ]
  },
  {
    /**
     * Routes which require a business entity
     */
    path: `${DEFAULT_SEGMENT}/:businessEntityUuid`,
    element: (
      <Authenticated>
        <BusinessEntityGate>
          <Template>
            <GlobalComponents>
              <PostRouting />
            </GlobalComponents>
          </Template>
        </BusinessEntityGate>
      </Authenticated>
    ),
    children: [
      // change the index to home once ZenDash is federating
      { index: true, element: <Pages.HomeGateway /> },
      { path: 'banking/*', element: <MFE.BankingUi /> },
      { path: 'business-profile', element: <MFE.ZenDash exportName="EntityProfile" /> },
      { path: 'clients/*', element: <MFE.BusinessOperationsUi /> },
      { path: 'compliance/*', element: <MFE.ComplianceUi /> },
      { path: 'documents/*', element: <MFE.DocumentsUi /> },
      { path: 'education-studio/*', element: <MFE.EducationUi /> },
      { path: 'invoicing/*', element: <MFE.InvoicingUi /> },
      { path: 'marketing-tools/*', element: <MFE.WebsitesUi /> },
      { path: 'money/*', element: <MFE.MoneyUi /> },
      { path: 'orders/:orderTaskUuid', element: <MFE.ZenDash exportName="EntityOrderView" /> },
      { path: 'subscription-hub/*', element: <MFE.EcommServicesUi /> },
      { path: 'worry-free-compliance', element: <MFE.ZenDash exportName="EntityWorryFreeCompliance" /> },
      { path: 'insights/*', element: <MFE.InsightsUi /> },
      { path: ':productId', element: <Pages.ProductLanding /> },
      /* THIS IS FOR DEBUGGING PURPOSES ONLY */
      ...(DEPLOY_ENV !== 'production'
        ? [
          { path: 'x/*', element: <MFE.AppTemplateUi /> },
          { path: 'loading', element: <PageLoading /> }
        ]
        : [])
    ]
  },
  {
    path: '*',
    element: (
      <ClickstreamWrapper>
        <PageError>
          The requested route could not be found.
        </PageError>
      </ClickstreamWrapper>
    )
  }
]);

export function DirectRoutes() {
  return (
    // ROOT_SEGMENT is used to identify where the router is mounted at.
    // Since this is a direct entry router, the root is the MFE's DEFAULT_SEGMENT
    <EnvProvider env={{ ROOT_SEGMENT: DEFAULT_SEGMENT }}>
      <RouterProvider router={router} />
    </EnvProvider>
  );
}
